import React, { useState, useEffect } from 'react';
import { Board } from './types';
import { getBoard, addParticipantByName } from './lib/proxy-client';
import { getOrCreateGuest, showAlert, shareLink, getInviteLink } from './lib/vk-bridge';
import { VKUser } from './types';
import Footer from './components/Footer';

const JoinPage: React.FC = () => {
  const [boardId, setBoardId] = useState('');
  const [board, setBoard] = useState<Board | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  const [currentUser, setCurrentUser] = useState<VKUser | null>(null);

  useEffect(() => {
    const init = async () => {
      const user = await getOrCreateGuest();
      setCurrentUser(user);

      const pathBoardId = window.location.pathname.split('/join/')[1];
      if (pathBoardId) {
        setBoardId(pathBoardId);
        loadBoard(pathBoardId);
      }
    };
    init();
  }, []);

  const loadBoard = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const boardData = await getBoard(id);
      setBoard(boardData);
    } catch (err) {
      console.error('Ошибка загрузки доски:', err);
      setError('Доска не найдена или недоступна');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    if (!board || !boardId || !currentUser) return;

    try {
      setJoining(true);
      
      const existingParticipant = board.participants?.find(
        p => p.name === currentUser.first_name || p.profile?.telegram_id === currentUser.id
      );

      if (existingParticipant) {
        setJoined(true);
        window.location.href = `/vk/board/${boardId}`;
        return;
      }

      await addParticipantByName(boardId, currentUser.first_name, currentUser.id);
      window.location.href = `/vk/board/${boardId}`;
      
    } catch (err) {
      console.error('Ошибка присоединения:', err);
      showAlert('Не удалось присоединиться к доске');
    } finally {
      setJoining(false);
    }
  };

  const handleShare = () => {
    const link = getInviteLink(boardId);
    shareLink(link);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column' }}>
        <div style={{ fontSize: '48px', marginBottom: '10px' }}>🍽️</div>
        <div>Загрузка...</div>
      </div>
    );
  }

  if (error || !board) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', marginTop: '40px' }}>
        <h2>❌ Ошибка</h2>
        <p>{error || 'Доска не найдена'}</p>
        <button onClick={() => window.location.reload()} style={{ marginTop: '20px', padding: '10px 20px' }}>
          Обновить
        </button>
      </div>
    );
  }

  const isAlreadyJoined = currentUser && board.participants?.some(
    p => p.name === currentUser.first_name || p.profile?.telegram_id === currentUser.id
  );

  const restaurantName = board.restaurant_name || board.restaurant?.name || 'Ресторан';
  const date = new Date(board.created_at).toLocaleDateString('ru-RU');
  const creator = board.participants?.find(p => p.is_creator)?.name || 'Создатель';

  return (
    <div style={{ 
      padding: '24px 20px 40px 20px',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'var(--vk-bg, #fff)',
      color: 'var(--vk-text, #000)',
      boxSizing: 'border-box'
    }}>
      <style>{`
        .header-section { text-align: center; margin-bottom: 32px; margin-top: 10px; }
        .inviter-badge { font-size: 16px; font-weight: 500; }
        .inviter-badge span { color: var(--vk-accent, #4680C2); font-weight: 600; }
        .info-card {
          background: var(--vk-secondary-bg, #f5f5f5);
          border-radius: 20px; padding: 30px 20px; text-align: center;
          margin-bottom: 32px; display: flex; flex-direction: column; gap: 8px;
        }
        .restaurant-name { font-size: 24px; font-weight: 700; line-height: 1.2; }
        .receipt-date { color: var(--vk-hint, #888); font-size: 14px; }
        .total-price { font-size: 36px; font-weight: 800; margin-top: 12px; color: var(--vk-accent, #4680C2); }
        .main-action { margin-bottom: 40px; width: 100%; }
        .btn-join {
          width: 100%; padding: 18px; border-radius: 16px;
          background-color: var(--vk-accent, #4680C2);
          color: #fff; font-size: 18px; font-weight: 600;
          border: none; cursor: pointer;
          box-shadow: 0 4px 12px rgba(70, 128, 194, 0.3);
          transition: transform 0.1s;
        }
        .btn-join:active { transform: scale(0.98); }
        .btn-outline {
          background: transparent; color: var(--vk-accent, #4680C2);
          border: none; padding: 10px; font-size: 14px; margin-top: 12px; width: 100%; cursor: pointer;
        }
        .participants-section { text-align: center; }
        .section-label {
          font-size: 13px; text-transform: uppercase; letter-spacing: 1px;
          color: var(--vk-hint, #aaa); margin-bottom: 16px;
        }
        .chips-container { display: flex; flex-wrap: wrap; justify-content: center; gap: 8px; }
        .user-chip {
          background: var(--vk-secondary-bg, #f0f0f0);
          padding: 8px 14px; border-radius: 12px; font-size: 14px;
          display: flex; align-items: center; gap: 6px;
        }
      `}</style>

      <div className="header-section">
        <div style={{ fontSize: '42px', marginBottom: '16px' }}>👋</div>
        <div className="inviter-badge">
          <span>{creator}</span> приглашает вас<br/>разделить чек
        </div>
      </div>
      
      <div className="info-card">
        <div className="restaurant-name">{restaurantName}</div>
        <div className="receipt-date">{date}</div>
        <div className="total-price">{board.total_amount.toLocaleString('ru-RU')} ₽</div>
      </div>

      <div className="main-action">
        {isAlreadyJoined || joined ? (
          <button className="btn-join" onClick={() => window.location.href = `/vk/board/${boardId}`}>
            Перейти к чеку →
          </button>
        ) : (
          <button className="btn-join" onClick={handleJoin} disabled={joining}>
            {joining ? 'Входим...' : 'Присоединиться'}
          </button>
        )}
        <button className="btn-outline" onClick={handleShare}>
          🔗 Поделиться ссылкой
        </button>
      </div>

      <div className="participants-section">
        <div className="section-label">Уже участвуют</div>
        <div className="chips-container">
          {board.participants?.map((p) => (
            <div key={p.id} className="user-chip">
              {p.name} {p.is_creator ? '👑' : ''}
            </div>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default JoinPage;
